/**
 * ApplicationsTab — shared tab content showing job/tuition applications.
 * Used in StudentDashboard and TutorDashboard.
 *
 * Props:
 *   applications - array of application objects
 */
import React from 'react';
import { FileText, Clock } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';

const ApplicationsTab = ({ applications = [] }) => (
    <Card>
        <CardHeader>
            <CardTitle className="flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                My Applications
            </CardTitle>
            <CardDescription>Track your job and tuition applications</CardDescription>
        </CardHeader>
        <CardContent>
            {applications.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No applications yet. Start applying to jobs or tutoring opportunities!</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {applications.map((app) => (
                        <div key={app.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Badge variant={app.target_type === 'job' ? 'default' : 'secondary'}>
                                            {app.target_type === 'job' ? 'Job' : 'Tuition'}
                                        </Badge>
                                        <h3 className="font-semibold">{app.target_title}</h3>
                                    </div>
                                    {app.target_details && (
                                        <div className="text-sm text-gray-600 space-y-1">
                                            {app.target_type === 'job' && (
                                                <>
                                                    <p><strong>Organization:</strong> {app.target_details.organization}</p>
                                                    <p><strong>Location:</strong> {app.target_details.location}</p>
                                                    <p><strong>Salary:</strong> {app.target_details.salary}</p>
                                                </>
                                            )}
                                            {app.target_type === 'tuition_post' && (
                                                <>
                                                    <p><strong>Subjects:</strong> {app.target_details.subjects?.join(', ')}</p>
                                                    <p><strong>City:</strong> {app.target_details.city}</p>
                                                    <p><strong>Budget:</strong> {app.target_details.budget}</p>
                                                </>
                                            )}
                                        </div>
                                    )}
                                    <p className="text-xs text-gray-500 mt-2">
                                        <Clock className="h-3 w-3 inline mr-1" />
                                        Applied: {new Date(app.applied_at).toLocaleDateString()}
                                    </p>
                                </div>
                                <Badge variant={app.status === 'contacted' ? 'success' : 'secondary'}>
                                    {app.status}
                                </Badge>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </CardContent>
    </Card>
);

export default ApplicationsTab;
